import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { EyeIcon } from "@heroicons/react/24/outline";
import { PageLoader } from "../../ui";
import VaptSeverityBadge from "../components/VaptSeverityBadge";
import VaptStatusBadge from "../components/VaptStatusBadge";
import { listVaptFindingsApi } from "../../../services/axios/AuthApis/VAPTApis";
import { VAPT_SEVERITIES, VAPT_VALIDATION_STATUSES, VAPT_REMEDIATION_STATUSES } from "../utils/vaptConstants";
import { DataTable } from "../../common/DataTable";

const asArray = (data) => (Array.isArray(data) ? data : data?.results || []);

const VaptFindings = () => {
  const navigate = useNavigate();
  const [findings, setFindings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    listVaptFindingsApi()
      .then((data) => {
        setFindings(asArray(data).map((f) => ({ ...f, id: f.finding_id })));
        setLoading(false);
      })
      .catch((err) => {
        setError(err?.message || "Failed to load findings.");
        setLoading(false);
      });
  }, []);

  if (loading) return <PageLoader title="Loading findings…" />;

  if (error) {
    return (
      <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
        <p className="text-gray-500">{error}</p>
      </div>
    );
  }

  const columns = [
    {
      id: "title",
      accessorFn: (f) => f.title,
      header: "Finding",
      enableSorting: false,
      meta: { filter: { variant: "text", label: "Finding", placeholder: "Search finding" } },
      cell: ({ row }) => <span>{row.original.title}</span>,
    },
    {
      id: "severity",
      accessorFn: (f) => f.severity,
      header: "Severity",
      enableSorting: false,
      meta: {
        filter: {
          variant: "select",
          label: "Severity",
          placeholder: "All severities",
          options: VAPT_SEVERITIES.map((v) => ({ value: v, label: v })),
        },
      },
      cell: ({ row }) => <VaptSeverityBadge severity={row.original.severity} size="sm" />,
    },
    {
      id: "target_display",
      accessorFn: (f) => f.target_display,
      header: "Target",
      enableSorting: false,
      meta: { filter: { variant: "text", label: "Target", placeholder: "Search target" } },
      cell: ({ row }) => <span>{row.original.target_display ?? "—"}</span>,
    },
    {
      id: "validation_status",
      accessorFn: (f) => f.validation_status,
      header: "Validation",
      enableSorting: false,
      meta: {
        filter: {
          variant: "select",
          label: "Validation",
          placeholder: "All validation statuses",
          options: VAPT_VALIDATION_STATUSES.map((v) => ({ value: v, label: v })),
        },
      },
      cell: ({ row }) => <VaptStatusBadge status={row.original.validation_status} size="sm" />,
    },
    {
      id: "finding_status",
      accessorFn: (f) => f.finding_status,
      header: "Status",
      enableSorting: false,
      meta: {
        filter: {
          variant: "select",
          label: "Status",
          placeholder: "All remediation statuses",
          options: VAPT_REMEDIATION_STATUSES.map((v) => ({ value: v, label: v })),
        },
      },
      cell: ({ row }) => <VaptStatusBadge status={row.original.finding_status} size="sm" />,
    },
    {
      id: "cvss_score",
      accessorFn: (f) => f.cvss_score,
      header: "CVSS",
      enableSorting: false,
      cell: ({ row }) => <span>{row.original.cvss_score ?? "—"}</span>,
    },
    {
      id: "actions",
      header: "Actions",
      enableSorting: false,
      cell: ({ row }) => (
        <button
          type="button"
          onClick={() => navigate(`/ai-vapt/findings/${row.original.finding_id}`)}
          title="View finding details"
          className="inline-flex items-center justify-center w-7 h-7 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
        >
          <EyeIcon className="w-4 h-4" />
        </button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-bold text-gray-900">Findings</h2>
        <p className="text-sm text-gray-500">{findings.length} findings</p>
      </div>

      <DataTable
        columns={columns}
        data={findings}
        getRowId={(f) => String(f.finding_id)}
        emptyMessage="No findings found."
        enableColumnVisibility={false}
        enableColumnResizing
        enableColumnFilters
        maxBodyHeight="65vh"
      />
    </div>
  );
};

export default VaptFindings;
